// Ядро игровой части: звук, ролики, искры и вирус. Перенесено из прототипа
// (prototypes/1/js/core.js) почти без изменений — механика и ощущение те же,
// изменилось только то, что состояние больше не хранится в localStorage.

// Механики умеют отдавать отклик в Telegram, но не зависят от его SDK:
// глобальный тип объявляет приложение, здесь достаточно узкого вида.
type HapticApi = { impactOccurred: (style: "light" | "medium" | "heavy") => void };

const telegramHaptic = (): HapticApi | undefined =>
  (globalThis as { Telegram?: { WebApp?: { HapticFeedback?: HapticApi } } }).Telegram?.WebApp
    ?.HapticFeedback;

export const $ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T | null =>
  root.querySelector<T>(sel);

export const $$ = <T extends Element = HTMLElement>(sel: string, root: ParentNode = document): T[] =>
  Array.from(root.querySelectorAll<T>(sel));

export const rand = (min: number, max: number): number => min + Math.random() * (max - min);
export const randInt = (min: number, max: number): number => Math.floor(rand(min, max + 1));
export const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export function shuffle<T>(list: T[]): T[] {
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [list[i], list[j]] = [list[j]!, list[i]!];
  }
  return list;
}

/** Вибрация: в Telegram отдаём её клиенту, вне его — браузеру */
export const vibrate = (pattern: number | number[]): void => {
  const haptic = telegramHaptic();
  if (haptic) {
    const strength = Array.isArray(pattern) ? pattern.length : pattern;
    haptic.impactOccurred(strength > 60 ? "heavy" : strength > 20 ? "medium" : "light");
    return;
  }
  if (navigator.vibrate) navigator.vibrate(pattern);
};

// ---------- Звук ----------

export const sfx = (() => {
  let ctx: AudioContext | null = null;
  let muted = false;

  function unlock(): void {
    if (!ctx) {
      const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AC) return;
      ctx = new AC();
    }
    if (ctx.state === "suspended") void ctx.resume();
  }

  function setMuted(value: boolean): void {
    muted = value;
  }

  function isMuted(): boolean {
    return muted;
  }

  function tone(
    freq: number,
    dur: number,
    { type = "square", vol = 0.06, to = null, delay = 0 }: { type?: OscillatorType; vol?: number; to?: number | null; delay?: number } = {},
  ): void {
    if (!ctx || muted) return;
    const t = ctx.currentTime + delay;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t);
    if (to) osc.frequency.exponentialRampToValueAtTime(to, t + dur);
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(vol, t + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + dur + 0.02);
  }

  function noise(
    dur: number,
    {
      vol = 0.2,
      from = 800,
      to = 100,
      filter = "lowpass",
      delay = 0,
      attack = 0.08,
    }: { vol?: number; from?: number; to?: number; filter?: BiquadFilterType; delay?: number; attack?: number } = {},
  ): void {
    if (!ctx || muted) return;
    const t = ctx.currentTime + delay;
    const len = Math.floor(ctx.sampleRate * dur);
    const buf = ctx.createBuffer(1, len, ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    const flt = ctx.createBiquadFilter();
    flt.type = filter;
    flt.frequency.setValueAtTime(from, t);
    flt.frequency.exponentialRampToValueAtTime(to, t + dur);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.exponentialRampToValueAtTime(vol, t + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    src.connect(flt).connect(gain).connect(ctx.destination);
    src.start(t);
  }

  return {
    unlock,
    setMuted,
    isMuted,
    lampOn: () => tone(880, 0.08, { vol: 0.05 }),
    lampOff: () => tone(420, 0.07, { vol: 0.04 }),
    tinker: () => tone(rand(1400, 2600), 0.03, { vol: 0.02 }),
    squeak: () => tone(420, 0.3, { type: "sine", vol: 0.14, to: 1500 }),
    run: () => {
      for (let i = 0; i < 7; i++) tone(rand(260, 340), 0.04, { type: "triangle", vol: 0.08, delay: i * 0.08 });
    },
    settle: () => tone(1250, 0.05, { type: "sine", vol: 0.05 }),
    success: () => {
      [523, 659, 784, 1047].forEach((f, i) => tone(f, 0.22, { type: "triangle", vol: 0.09, delay: i * 0.09 }));
    },
    door: () => {
      noise(1.9, { vol: 0.25, from: 500, to: 90 });
      tone(58, 1.8, { type: "sawtooth", vol: 0.05, to: 38 });
    },
    whoosh: () => noise(1.1, { vol: 0.12, from: 400, to: 6000, filter: "highpass" }),
    city: () => {
      noise(4.5, { vol: 0.07, from: 300, to: 900, filter: "bandpass", attack: 1.2 });
      tone(73, 4.2, { type: "sine", vol: 0.05, to: 70 });
    },
    zoom: () => noise(1.1, { vol: 0.1, from: 300, to: 3000, filter: "bandpass", attack: 0.6 }),
    cabinetOpen: () => {
      tone(120, 0.12, { type: "square", vol: 0.05 });
      noise(1.2, { vol: 0.12, from: 2400, to: 600, filter: "bandpass", delay: 0.08 });
    },
    /** at — через сколько секунд створки сойдутся и стукнут */
    cabinetClose: (at = 0.95) => {
      noise(at, { vol: 0.1, from: 600, to: 2400, filter: "bandpass" });
      noise(0.35, { vol: 0.35, from: 400, to: 60, delay: at, attack: 0.01 });
      tone(70, 0.3, { type: "sine", vol: 0.16, to: 40, delay: at });
    },
    pullback: () => noise(0.9, { vol: 0.07, from: 1800, to: 300, filter: "bandpass", attack: 0.3 }),
    corrupt: () => tone(rand(90, 160), 0.12, { type: "sawtooth", vol: 0.04, to: 60 }),
    boot: () => {
      tone(660, 0.1, { type: "sine", vol: 0.08 });
      tone(990, 0.16, { type: "sine", vol: 0.08, delay: 0.11 });
    },
    key: (i = 0) => tone(600 + i * 90, 0.05, { vol: 0.045 }),
    undo: () => tone(300, 0.06, { vol: 0.04, to: 200 }),
    deny: () => tone(150, 0.14, { type: "square", vol: 0.05 }),
    start: () => {
      tone(440, 0.09, { type: "triangle", vol: 0.08 });
      tone(880, 0.14, { type: "triangle", vol: 0.08, delay: 0.09 });
    },
    step: () => tone(rand(200, 240), 0.05, { type: "triangle", vol: 0.07 }),
    bump: () => tone(110, 0.1, { type: "square", vol: 0.06, to: 80 }),
    repair: () => {
      [880, 1175, 1568].forEach((f, i) => tone(f, 0.12, { type: "sine", vol: 0.07, delay: i * 0.06 }));
      noise(0.25, { vol: 0.05, from: 4000, to: 8000, filter: "highpass" });
    },
    miss: () => tone(260, 0.12, { type: "sine", vol: 0.05, to: 200 }),
    error: () => {
      tone(110, 0.5, { type: "sawtooth", vol: 0.09 });
      tone(116, 0.5, { type: "sawtooth", vol: 0.07 });
    },
    scan: () => noise(0.8, { vol: 0.06, from: 800, to: 5000, filter: "bandpass" }),
    pick: () => tone(700, 0.04, { type: "triangle", vol: 0.05 }),
    drop: () => {
      tone(320, 0.07, { type: "triangle", vol: 0.07 });
      noise(0.12, { vol: 0.06, from: 2000, to: 400, attack: 0.01 });
    },
    lcd: () => tone(2200, 0.012, { vol: 0.012 }),
    type: () => tone(rand(1500, 2100), 0.012, { vol: 0.014 }),
    pop: () => {
      tone(880, 0.06, { type: "sine", vol: 0.07 });
      tone(1320, 0.12, { type: "sine", vol: 0.06, delay: 0.05 });
    },
    dash: () => noise(0.7, { vol: 0.1, from: 300, to: 3000, filter: "bandpass", attack: 0.2 }),
    servo: () => {
      tone(180, 0.45, { type: "sawtooth", vol: 0.035, to: 320 });
      noise(0.45, { vol: 0.04, from: 1200, to: 500, filter: "bandpass" });
    },
  };
})();

// ---------- Ролики ----------
// Всё, что ролик запускает, регистрируется здесь, чтобы «Пропустить»
// и выход из уровня могли остановить его в любой момент.

export class Cutscene {
  private anims: Animation[] = [];
  private timers: ReturnType<typeof setTimeout>[] = [];
  private holds: ((reason: Error) => void)[] = [];
  alive = true;

  track(anim: Animation): Animation {
    this.anims.push(anim);
    return anim;
  }

  later(ms: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const t = setTimeout(() => (this.alive ? resolve() : reject(new Error("cancelled"))), ms);
      this.timers.push(t);
    });
  }

  every(ms: number, fn: () => void): ReturnType<typeof setInterval> {
    const id = setInterval(fn, ms);
    this.timers.push(id);
    return id;
  }

  /** Пауза до события: done() продолжает ролик, остановка ролика его обрывает */
  hold(): { promise: Promise<void>; done: () => void } {
    let done!: () => void;
    const promise = new Promise<void>((resolve, reject) => {
      done = resolve;
      this.holds.push(reject);
    });
    return { promise, done };
  }

  stop(): void {
    this.alive = false;
    this.timers.forEach((t) => {
      clearTimeout(t);
      clearInterval(t);
    });
    this.anims.forEach((a) => a.cancel());
    this.holds.forEach((reject) => reject(new Error("cancelled")));
    this.anims = [];
    this.timers = [];
    this.holds = [];
  }
}

export function spawnSpark(container: HTMLElement, x: number, y: number, spread = 1): void {
  const s = document.createElement("div");
  s.className = "spark";
  s.style.setProperty("--c", ["#ff3df0", "#3ef0ff", "#ffb238"][Math.floor(Math.random() * 3)]!);
  s.style.left = `${x}%`;
  s.style.top = `${y}%`;
  container.appendChild(s);
  const dx = rand(-10, 14) * spread;
  const dy = rand(-14, 4) * spread;
  const a = s.animate(
    [
      { transform: "translate(0, 0) scale(1)", opacity: 1 },
      { transform: `translate(${dx}cqw, ${dy}cqw) scale(0.2)`, opacity: 0 },
    ],
    { duration: rand(300, 600), easing: "cubic-bezier(.2,.7,.4,1)" },
  );
  a.onfinish = () => s.remove();
}

// ---------- Вирус ----------

export const SPRITES = {
  back: "/assets/virus_back.webp",
  backHidden: "/assets/virus_back2.webp",
  scared: "/assets/virus_scared.webp",
  run: "/assets/virus_run.webp",
} as const;

export type SpriteName = keyof typeof SPRITES;

Object.values(SPRITES).forEach((src) => {
  const img = new Image();
  img.src = src;
});

export interface VirusParts {
  actor: HTMLElement;
  body: HTMLElement;
  img: HTMLImageElement;
  exclaim: HTMLElement;
}

/** Разметка актёра: .actor > .actor-body > img, и .exclaim рядом */
export function virusParts(actor: HTMLElement): VirusParts {
  return {
    actor,
    body: $(".actor-body", actor)!,
    img: $<HTMLImageElement>("img", actor)!,
    exclaim: $(".exclaim", actor)!,
  };
}

export function virusReset(v: VirusParts, sprite: SpriteName): void {
  v.actor.hidden = false;
  v.actor.style.transform = "";
  v.img.src = SPRITES[sprite];
}

export function virusTinker(cut: Cutscene, v: VirusParts): Animation {
  return cut.track(
    v.img.animate(
      [{ transform: "rotate(-3deg) translateY(0)" }, { transform: "rotate(3deg) translateY(-1.5%)" }],
      { duration: 240, iterations: Infinity, direction: "alternate", easing: "ease-in-out" },
    ),
  );
}

/** Замирает, оборачивается, пугается. Около 1.7 с. */
export async function virusScare(cut: Cutscene, v: VirusParts, tinker: Animation): Promise<void> {
  tinker.pause();
  await cut.later(350);
  tinker.cancel();

  await cut.track(
    v.body.animate([{ transform: "scaleX(1)" }, { transform: "scaleX(0)" }], {
      duration: 110,
      easing: "ease-in",
      fill: "forwards",
    }),
  ).finished;

  v.img.src = SPRITES.scared;
  sfx.squeak();
  vibrate(40);

  cut.track(
    v.body.animate(
      [
        { transform: "scaleX(0) translateY(0)" },
        { transform: "scaleX(1.05) translateY(-12%)", offset: 0.4 },
        { transform: "scaleX(1) translateY(0)" },
      ],
      { duration: 420, easing: "ease-out", fill: "forwards" },
    ),
  );
  cut.track(
    v.exclaim.animate(
      [
        { transform: "translateX(-50%) scale(0)", opacity: 0 },
        { transform: "translateX(-50%) scale(1.3)", opacity: 1, offset: 0.6 },
        { transform: "translateX(-50%) scale(1)", opacity: 1 },
      ],
      { duration: 300, delay: 80, easing: "ease-out", fill: "forwards" },
    ),
  );
  cut.track(
    v.img.animate([{ transform: "translateX(-2%)" }, { transform: "translateX(2%)" }], {
      duration: 60,
      iterations: 14,
      direction: "alternate",
      delay: 420,
    }),
  );
  await cut.later(1250);
}

/** Убегает вправо за край экрана. */
export function virusRun(cut: Cutscene, v: VirusParts): void {
  cut.track(v.exclaim.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 120, fill: "forwards" }));
  v.img.src = SPRITES.run;
  sfx.run();
  cut.track(
    v.img.animate([{ transform: "translateY(0)" }, { transform: "translateY(-4%)" }], {
      duration: 90,
      iterations: Infinity,
      direction: "alternate",
    }),
  );
  cut.track(
    v.actor.animate(
      [
        { transform: "translate(-50%, -50%)" },
        { transform: "translate(-38%, -52%)", offset: 0.25 },
        { transform: "translate(130%, -58%)" },
      ],
      { duration: 800, easing: "cubic-bezier(.5,0,.8,.6)", fill: "forwards" },
    ),
  );
}

export function virusHide(v: VirusParts): void {
  v.actor.hidden = true;
  v.actor.style.transform = "";
}

/** Разметка актёра одинакова во всех уровнях */
export const actorMarkup = (sprite: keyof typeof SPRITES = "back"): string => `
  <div class="actor" hidden>
    <div class="actor-body"><img src="${SPRITES[sprite]}" alt=""></div>
    <div class="exclaim">!</div>
  </div>`;
